import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
} from 'firebase/messaging'
import { getApp } from 'firebase/app'

import { apiCall, isBackendConfigured } from './api-call'

type PushRegistrationOutcome =
  | { status: 'registered'; token: string }
  | { status: 'denied' }
  | { status: 'unsupported' }
  | { status: 'misconfigured'; reason: string }
  | { status: 'error'; errorMessage: string }

const LOCAL_TOKEN_KEY = 'quickpass.pushToken.v1'

const readPersistedToken = (): string | null => {
  try {
    const stored = window.localStorage.getItem(LOCAL_TOKEN_KEY)
    if (stored === null || stored === '') {
      return null
    }
    return stored
  } catch {
    return null
  }
}

const persistToken = (token: string): void => {
  try {
    window.localStorage.setItem(LOCAL_TOKEN_KEY, token)
  } catch {
    // localStorage unavailable — ignore.
  }
}

const clearPersistedToken = (): void => {
  try {
    window.localStorage.removeItem(LOCAL_TOKEN_KEY)
  } catch {
    // localStorage unavailable — ignore.
  }
}

const buildServiceWorkerUrl = (): string => {
  const params = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
  })
  return `/firebase-messaging-sw.js?${params.toString()}`
}

const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  return await navigator.serviceWorker.register(buildServiceWorkerUrl(), {
    scope: '/',
  })
}

const buildMessagingInstance = (): Messaging | null => {
  try {
    return getMessaging(getApp())
  } catch (error) {
    console.warn('[quickpass-push] getMessaging failed', error)
    return null
  }
}

/**
 * Walks the renter through requesting browser notification permission, fetching
 * an FCM device token via the service worker, and persisting the token to the
 * backend. Idempotent — a registered token simply gets re-saved server-side so
 * `createdAt` stays fresh.
 *
 * @param {string} quickPassUserId - the renter id the token should attach to
 * @returns {Promise<PushRegistrationOutcome>}
 */
const requestQuickPassPushPermission = async (
  quickPassUserId: string,
): Promise<PushRegistrationOutcome> => {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? ''

  if (vapidKey === '') {
    return {
      status: 'misconfigured',
      reason: 'VITE_FIREBASE_VAPID_KEY is not set.',
    }
  }

  const supported = await isSupported().catch(() => false)
  if (supported === false) {
    return { status: 'unsupported' }
  }

  if (typeof Notification === 'undefined') {
    return { status: 'unsupported' }
  }

  if (
    typeof navigator === 'undefined' ||
    'serviceWorker' in navigator === false
  ) {
    return { status: 'unsupported' }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return { status: 'denied' }
  }

  try {
    const registration = await registerServiceWorker()
    const messaging = buildMessagingInstance()
    if (messaging === null) {
      return {
        status: 'misconfigured',
        reason: 'Firebase messaging is not initialised.',
      }
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    })

    if (token === '') {
      return {
        status: 'error',
        errorMessage: 'FCM returned an empty token.',
      }
    }

    persistToken(token)

    if (isBackendConfigured() === true) {
      const response = await apiCall({
        group: 'quickpass',
        command: 'registerpushtoken',
        data: { quickPassUserId, token },
      })
      if (response.code !== 200) {
        return { status: 'error', errorMessage: response.message }
      }
    }

    return { status: 'registered', token }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Push permission flow failed.'
    return { status: 'error', errorMessage: message }
  }
}

/**
 * Tears down a previously-registered token: drops it from the backend, the
 * local cache, and the browser. Safe to call when no token exists.
 *
 * @param {string} quickPassUserId - the renter id the token belonged to
 * @returns {Promise<void>}
 */
const unregisterQuickPassPush = async (
  quickPassUserId: string,
): Promise<void> => {
  const token = readPersistedToken()

  if (token === null) {
    return
  }

  clearPersistedToken()

  if (isBackendConfigured() === true) {
    try {
      await apiCall({
        group: 'quickpass',
        command: 'unregisterpushtoken',
        data: { quickPassUserId, token },
      })
    } catch (error) {
      console.warn('[quickpass-push] backend unregister failed', error)
    }
  }
}

/**
 * Pulls the locally persisted token without contacting the backend. Useful
 * for the profile UI to show "Push is on for this device" without round-trip.
 *
 * @returns {string | null}
 */
const getCachedQuickPassPushToken = (): string | null => {
  return readPersistedToken()
}

export {
  requestQuickPassPushPermission,
  unregisterQuickPassPush,
  getCachedQuickPassPushToken,
}
export type { PushRegistrationOutcome }
